import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  ELEMENT_LAYOUT,
  MONTSERRAT_LINE_HEIGHT,
  SIMPLE_LAYOUT,
  SIMPLE_LINE_WIDTHS,
} from "./layout/constants";
import { boxPos, centerX as toLabelCenterX, textPos } from "./labelGeometry";
import { LABEL_BOX, getFrameAsset } from "./labelAssets";
import { getVisualStyle } from "./labelVisual";
import { pickLayout } from "./pickLayout";
import {
  autographLineText,
  gradeDisplayCenterX,
  gradeLabelFontSize,
  gradeLineText,
} from "./gradeHelpers";
import { softClampFieldForCard } from "./limitString";
import { measureScreenTextWidth } from "./measureScreenText";
import { generateQrDataUrl } from "./qrCode";

const NO_SLOT = { x_offset: 0, y_offset: 0 };

/** Carta fora da seleção fica visível mas não editável (paridade com a tela antiga). */
const ReadOnlyContext = createContext(false);

const MONTSERRAT_BOLD = { family: '"Montserrat-Bold"', weight: "normal" };
const MONTSERRAT_LIGHT = { family: '"Montserrat-Light"', weight: "normal" };
// Fonte CSS da família declarada no layout (constants.js): a tela desenha com a
// mesma face que o PDF registra e que o fontkit mede.
const fontForFamily = (family) =>
  /Light/i.test(family) ? MONTSERRAT_LIGHT : MONTSERRAT_BOLD;

const baseTextStyle = (font, fontSize, color) => ({
  position: "absolute",
  fontFamily: font.family,
  fontWeight: font.weight,
  fontSize,
  // ascent+descent: zera o half-leading, então a baseline cai no mesmo ponto
  // que no PDF (topo da caixa + ascender).
  lineHeight: MONTSERRAT_LINE_HEIGHT,
  color,
  whiteSpace: "pre",
  // Inputs herdam Open Sans do body se a face custom não estiver pronta;
  // forçar evita flash da fonte errada.
  WebkitFontSmoothing: "antialiased",
});

/** Input transparente ocupando exatamente o espaço do texto no PDF. */
const FieldInput = ({
  value,
  onChange,
  font,
  fontSize,
  color,
  left,
  top,
  width,
  align = "left",
  overflow,
  ariaLabel,
  dataField,
}) => {
  const readOnly = useContext(ReadOnlyContext);
  const measured = measureScreenTextWidth(
    value || "",
    font.family.replace(/"/g, ""),
    fontSize,
    font.weight
  );
  // Direita: largura colada no texto, âncora na borda direita (senão o teto da
  // área útil — ex. numeração ~37 pt — esconde o valor enquanto edita).
  const contentWidth = Math.max(measured + fontSize * 0.6, fontSize * 2);
  const inputWidth = align === "right" ? contentWidth : width ?? contentWidth;
  const boxLeft = align === "right" ? left - inputWidth : left;

  return (
    <input
      aria-label={ariaLabel}
      data-tag-error-field={dataField}
      value={value ?? ""}
      readOnly={readOnly}
      onChange={(event) => onChange(event.target.value)}
      spellCheck={false}
      style={{
        ...baseTextStyle(font, fontSize, color),
        left: boxLeft,
        top,
        width: inputWidth,
        textAlign: align,
        padding: 0,
        margin: 0,
        border: "none",
        outline: overflow ? "1px solid #dc2626" : "none",
        background: overflow ? "rgba(220,38,38,0.15)" : "transparent",
        borderRadius: 1,
        // Alguns browsers herdam cor do tema no input; força o fill.
        WebkitTextFillColor: color,
        caretColor: color,
      }}
    />
  );
};

const StaticText = ({ children, font, fontSize, color, left, top, width, align = "left" }) => {
  if (children === null || children === undefined || String(children).trim() === "") return null;
  // Centro: ancora no próprio eixo e traduz −50% da largura do texto (mais
  // estável que textAlign numa caixa larga, que em alguns browsers falha).
  const centered = align === "center";
  const rightAligned = align === "right" && width;
  return (
    <span
      style={{
        ...baseTextStyle(font, fontSize, color),
        left: rightAligned ? left - width : left,
        top,
        // A caixa do texto à direita cobre a linha inteira (paridade com o
        // PDF) e é pintada depois dos inputs: sem isto ela vira um escudo que
        // engole o clique do campo editável da mesma linha.
        pointerEvents: "none",
        ...(centered
          ? { transform: "translateX(-50%)", textAlign: "center", whiteSpace: "nowrap" }
          : { width, textAlign: align }),
      }}
    >
      {String(children)}
    </span>
  );
};

/** Parte editável do composto `| ano | idioma | num`, largura colada no texto. */
const InlineInput = ({ value, onChange, color, ariaLabel }) => {
  const readOnly = useContext(ReadOnlyContext);
  const width = Math.max(
    measureScreenTextWidth(
      value || "",
      MONTSERRAT_LIGHT.family.replace(/"/g, ""),
      4.5,
      MONTSERRAT_LIGHT.weight
    ) + 1,
    4
  );
  return (
    <input
      aria-label={ariaLabel}
      value={value ?? ""}
      readOnly={readOnly}
      onChange={(event) => onChange(event.target.value)}
      spellCheck={false}
      style={{
        fontFamily: MONTSERRAT_LIGHT.family,
        fontSize: 4.5,
        lineHeight: 1,
        color,
        width,
        padding: 0,
        margin: 0,
        border: "none",
        outline: "none",
        background: "transparent",
      }}
    />
  );
};

const WithSubgradesFields = ({ card, textColor, overflowKeys, onField, qrDataUrl }) => {
  const gradeCenter = toLabelCenterX(gradeDisplayCenterX(NO_SLOT));
  const nomeNotaSize = gradeLabelFontSize(card.notaGeral);

  const at = (key, fontSize) => textPos(ELEMENT_LAYOUT[key], fontSize);
  const nomePos = at("nome", 8.5);
  const editionPos = at("edition", 4.5);
  const raridadePos = at("raridade", 4.5);
  const infosPos = at("infos", 4.5);
  const notaGeralPos = at("nota_geral", 27);
  const nomeNotaPos = at("nome_nota", nomeNotaSize);
  const serialPos = at("serial", 5.5);

  return (
    <>
      <FieldInput
        value={card.nome}
        onChange={(next) =>
          onField("nome", softClampFieldForCard("nome", next, card, card.nome))
        }
        font={MONTSERRAT_BOLD}
        fontSize={8.5}
        color={textColor}
        left={nomePos.left}
        top={nomePos.top}
        overflow={overflowKeys?.has?.("nome")}
        ariaLabel="Nome"
        dataField={`${card.serial}::nome`}
      />
      <FieldInput
        value={card.edition}
        onChange={(next) =>
          onField("edition", softClampFieldForCard("edicao", next, card, card.edition))
        }
        font={MONTSERRAT_LIGHT}
        fontSize={4.5}
        color={textColor}
        left={editionPos.left}
        top={editionPos.top}
        overflow={overflowKeys?.has?.("edicao")}
        ariaLabel="Edição"
        dataField={`${card.serial}::edicao`}
      />
      <FieldInput
        value={card.raridade}
        onChange={(next) =>
          onField("raridade", softClampFieldForCard("raridade", next, card, card.raridade))
        }
        font={MONTSERRAT_LIGHT}
        fontSize={4.5}
        color={textColor}
        left={raridadePos.left}
        top={raridadePos.top}
        overflow={overflowKeys?.has?.("raridade")}
        ariaLabel="Raridade"
        dataField={`${card.serial}::raridade`}
      />

      <span
        data-tag-error-field={`${card.serial}::infos`}
        style={{
          ...baseTextStyle(MONTSERRAT_LIGHT, 4.5, textColor),
          left: infosPos.left,
          top: infosPos.top,
          display: "flex",
          alignItems: "flex-start",
          outline: overflowKeys?.has?.("infos") ? "1px solid #dc2626" : "none",
          background: overflowKeys?.has?.("infos") ? "rgba(220,38,38,0.15)" : "transparent",
        }}
      >
        <span>| </span>
        <InlineInput
          value={card.ano}
          onChange={(next) =>
            onField("ano", softClampFieldForCard("ano", next, card, card.ano))
          }
          color={textColor}
          ariaLabel="Ano"
        />
        <span> | </span>
        <InlineInput
          value={card.idioma}
          onChange={(next) =>
            onField("idioma", softClampFieldForCard("idioma", next, card, card.idioma))
          }
          color={textColor}
          ariaLabel="Idioma"
        />
        <span> | </span>
        <InlineInput
          value={card.num}
          onChange={(next) =>
            onField("num", softClampFieldForCard("num", next, card, card.num))
          }
          color={textColor}
          ariaLabel="Numeração"
        />
      </span>

      <StaticText
        font={MONTSERRAT_LIGHT}
        fontSize={6.5}
        color={textColor}
        left={at("surface", 6.5).left}
        top={at("surface", 6.5).top}
      >
        {`Surface - ${card.notaSurface}`}
      </StaticText>
      <StaticText
        font={MONTSERRAT_LIGHT}
        fontSize={6.5}
        color={textColor}
        left={at("centering", 6.5).left}
        top={at("centering", 6.5).top}
      >
        {`Centering - ${card.notaCenter}`}
      </StaticText>
      <StaticText
        font={MONTSERRAT_LIGHT}
        fontSize={6.5}
        color={textColor}
        left={at("edges", 6.5).left}
        top={at("edges", 6.5).top}
      >
        {`Edges - ${card.notaEdge}`}
      </StaticText>
      <StaticText
        font={MONTSERRAT_LIGHT}
        fontSize={6.5}
        color={textColor}
        left={at("corners", 6.5).left}
        top={at("corners", 6.5).top}
      >
        {`Corners - ${card.notaCorner}`}
      </StaticText>

      <StaticText
        font={MONTSERRAT_BOLD}
        fontSize={27}
        color={textColor}
        left={gradeCenter}
        top={notaGeralPos.top}
        align="center"
      >
        {card.notaGeral}
      </StaticText>
      <StaticText
        font={MONTSERRAT_LIGHT}
        fontSize={nomeNotaSize}
        color={textColor}
        left={gradeCenter}
        top={nomeNotaPos.top}
        align="center"
      >
        {card.nome_nota}
      </StaticText>
      <StaticText
        font={MONTSERRAT_LIGHT}
        fontSize={5.5}
        color={textColor}
        left={gradeCenter}
        top={serialPos.top}
        align="center"
      >
        {card.serial}
      </StaticText>
      <QrPreview layout={ELEMENT_LAYOUT.qr} dataUrl={qrDataUrl} />
    </>
  );
};

const SimpleFields = ({ card, textColor, overflowKeys, onField, scenario, qrDataUrl }) => {
  const showAuthentic = scenario === "authentic" || scenario === "authentic_auto";
  const showGrade = scenario === "no_subgrades" || scenario === "no_subgrades_auto";
  const showAuto = scenario === "authentic_auto" || scenario === "no_subgrades_auto";

  const at = (key) => {
    const layout = SIMPLE_LAYOUT[key];
    const [fontFamily, fontSize] = layout.font;
    return {
      ...textPos(layout, fontSize),
      fontSize,
      font: fontForFamily(fontFamily),
      // Direita: caixa = linha inteira, igual ao PDF (nunca quebra nem vaza).
      width: SIMPLE_LINE_WIDTHS[key],
    };
  };

  const editable = (key, value, field, clampKey, overflowKey) => {
    const pos = at(key);
    return (
      <FieldInput
        value={value}
        onChange={(next) =>
          onField(field, softClampFieldForCard(clampKey, next, card, value))
        }
        font={pos.font}
        fontSize={pos.fontSize}
        color={textColor}
        left={pos.left}
        top={pos.top}
        overflow={overflowKeys?.has?.(overflowKey)}
        ariaLabel={field}
        dataField={`${card.serial}::${overflowKey}`}
      />
    );
  };

  const readonly = (key, value, align = "left") => {
    const pos = at(key);
    return (
      <StaticText
        font={pos.font}
        fontSize={pos.fontSize}
        color={textColor}
        left={pos.left}
        top={pos.top}
        width={align === "right" ? pos.width : undefined}
        align={align}
      >
        {value}
      </StaticText>
    );
  };

  // Ano / idioma / numeração: área útil individual (não o composto infos).
  const simpleInput = (key, field, value, overflowKey, align = "left") => {
    const pos = at(key);
    return (
      <FieldInput
        value={value}
        onChange={(next) =>
          onField(field, softClampFieldForCard(overflowKey, next, card, value))
        }
        font={pos.font}
        fontSize={pos.fontSize}
        color={textColor}
        left={pos.left}
        top={pos.top}
        width={align === "right" ? pos.width : undefined}
        align={align}
        overflow={overflowKeys?.has?.(overflowKey)}
        ariaLabel={field}
        dataField={`${card.serial}::${overflowKey}`}
      />
    );
  };

  return (
    <>
      {editable("name", card.nome, "nome", "nome", "nome")}
      {editable("edition", card.edition, "edition", "edicao", "edicao")}
      {showAuthentic ? readonly("right_top", "AUTHENTIC", "right") : null}
      {simpleInput("number", "num", card.num, "numeracao", "right")}
      {editable("raridade", card.raridade, "raridade", "raridade", "raridade")}
      {showGrade ? readonly("right_middle", gradeLineText(card), "right") : null}
      {simpleInput("ano", "ano", card.ano, "ano")}
      {showAuto ? readonly("right_auto", autographLineText(card.notaAutografo), "right") : null}
      {simpleInput("idioma", "idioma", card.idioma, "idioma")}
      {readonly("serial", card.serial, "right")}
      <QrPreview layout={SIMPLE_LAYOUT.qr} dataUrl={qrDataUrl} />
    </>
  );
};

/** QR na mesma posição/rotação do PDF (verso da dobra). */
const QrPreview = ({ layout, dataUrl }) => {
  if (!dataUrl) return null;
  const pos = boxPos(layout);
  return (
    <img
      alt=""
      src={dataUrl}
      draggable={false}
      style={{
        position: "absolute",
        left: pos.left,
        top: pos.top,
        width: pos.width,
        height: pos.height,
        transform: "rotate(180deg)",
        pointerEvents: "none",
        userSelect: "none",
      }}
    />
  );
};

/** Metade da etiqueta: dobra no meio (frente = topo, verso = base). */
const LABEL_FACE_HEIGHT = LABEL_BOX.height / 2;

/**
 * Etiqueta editável em tamanho real (pt) escalada por `scale`.
 * Por padrão mostra só a frente (metade de cima, editável). O verso (QR /
 * branding) aparece ao girar — conteúdo da base já impresso a 180°, então
 * rotacionamos o viewport para ler como na peça dobrada.
 */
const LabelCardEditor = ({ card, scale = 3, overflowKeys = null, onField, readOnly = false }) => {
  const visual = card?.visual || "majorca_legado";
  const layout = card?.scenario ? pickLayout(card.scenario) : null;
  const { textColor, fillColor } = getVisualStyle(visual);
  const frameSrc = useMemo(() => getFrameAsset(visual), [visual]);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [face, setFace] = useState("front");
  const showingBack = face === "back";

  useEffect(() => {
    let cancelled = false;
    if (!card?.serial) {
      setQrDataUrl(null);
      return undefined;
    }
    generateQrDataUrl(card.serial)
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [card?.serial]);

  const viewportW = LABEL_BOX.width * scale;
  const viewportH = LABEL_FACE_HEIGHT * scale;

  return (
    <ReadOnlyContext.Provider value={readOnly || showingBack}>
      <div className="flex flex-col gap-1.5 items-start">
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="text-xs px-2 py-1 rounded border border-[#d59106] bg-[#111] text-[#ffedd5] hover:bg-[#2a2a2a]"
            onClick={() => setFace((f) => (f === "front" ? "back" : "front"))}
            aria-pressed={showingBack}
            aria-label={showingBack ? "Ver frente da etiqueta" : "Ver verso da etiqueta"}
          >
            {showingBack ? "Ver frente" : "Ver verso"}
          </button>
          <span className="text-[11px] text-[#d59106]/80">
            {showingBack ? "Verso (após a dobra)" : "Frente (editável)"}
          </span>
        </div>

        <div
          style={{
            width: viewportW,
            height: viewportH,
            overflow: "hidden",
            position: "relative",
            flexShrink: 0,
            // Base impressa de cabeça para baixo; girar o recorte deixa o verso legível.
            transform: showingBack ? "rotate(180deg)" : undefined,
            backgroundColor: fillColor || "#FFFFFF",
          }}
        >
          <div
            style={{
              width: LABEL_BOX.width,
              height: LABEL_BOX.height,
              position: "absolute",
              left: 0,
              top: 0,
              transform: showingBack
                ? `scale(${scale}) translateY(-${LABEL_FACE_HEIGHT}px)`
                : `scale(${scale})`,
              transformOrigin: "top left",
              backgroundColor: fillColor || "#FFFFFF",
            }}
          >
            <img
              alt=""
              src={frameSrc}
              draggable={false}
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                width: LABEL_BOX.width,
                height: LABEL_BOX.height,
                pointerEvents: "none",
                userSelect: "none",
              }}
            />
            {layout === "with_subgrades" ? (
              <WithSubgradesFields
                card={card}
                textColor={textColor}
                overflowKeys={overflowKeys}
                onField={onField}
                qrDataUrl={qrDataUrl}
              />
            ) : layout ? (
              <SimpleFields
                card={card}
                textColor={textColor}
                overflowKeys={overflowKeys}
                onField={onField}
                scenario={card.scenario}
                qrDataUrl={qrDataUrl}
              />
            ) : null}
          </div>
        </div>
      </div>
    </ReadOnlyContext.Provider>
  );
};

export default LabelCardEditor;
export { LABEL_BOX, LABEL_FACE_HEIGHT };
